// src/components/Footer/Footer.jsx
import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Music4, MapPin, Phone, Mail } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { DASH_USER_QS } from "../../utils/nav";
import SocialLinks from "../Social/SocialLinks.jsx"; // ⬅️ keskitetyt some-linkit

// Yhtenäistetty route: sama kuin päävalikossa
const BLOG_ROUTE = "/blogi";

const Footer = () => {
  // Sama kirjautumistilan tunnistuslogiikka kuin päävalikossa
  const auth = useAuth?.() || {};
  const { user, currentUser, token, isAuthenticated } = auth;
  const userLike = user ?? currentUser ?? null;
  const hasUserId = !!(userLike && (userLike.id || userLike._id || userLike.user_id || userLike.email));
  const isAuthed = Boolean(typeof isAuthenticated === "boolean" ? isAuthenticated : (token || hasUserId));

  // 🔒 Piilota Dashboard-linkki onboardingin aikana (sama ajatus kuin headerissa)
  const location = useLocation();
  const norm = (p) => (p === "/" ? "/" : p.replace(/\/+$/, ""));
  const path = norm(location.pathname);
  const params = new URLSearchParams(location.search);
  const cameFromOnboarding =
    (params.get("from") || "").toLowerCase() === "onboarding" ||
    location.state?.fromOnboarding === true;

  // Huom. mukana molemmat privacy-reitit, jos käytössä eri nimillä
  const termsLikePaths = ["/kayttoehdot", "/ohjeet-ja-ehdot", "/tietosuoja", "/tietosuojaseloste"];
  const onboardingRoots = ["/rekisteroidy", "/verify-email", "/onboarding", "/setup"];
  const isOnboardingFlow =
    onboardingRoots.some((p) => path.startsWith(p)) || (termsLikePaths.includes(path) && cameFromOnboarding);

  const canShowDashboard = isAuthed && !isOnboardingFlow;

  return (
    <footer className="relative">
      {/* Neon-yläviiva */}
      <div className="h-2 w-full bg-gradient-to-r from-pink via-electric to-lemon dark:bg-gradient-to-r dark:from-electric/90 dark:via-primary/80 dark:to-white/80" />

      {/* Tausta + hehku */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black/95 dark:from-slate-950/90 dark:via-slate-950/95 dark:to-slate-950/95" />
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-pink/20 blur-3xl" />
        <div className="absolute -bottom-28 -right-20 h-72 w-72 rounded-full bg-electric/20 blur-3xl" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          {/* Linkkikolumnit – tasaisesti */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10">
            {/* Löydä esiintyjä */}
            <div>
              <h4 className="font-customFont2 text-xl text-white uppercase mb-3">Löydä esiintyjä</h4>
              <ul className="space-y-2 text-white/80 font-customFont1 text-xs md:text-lg">
                <li>
                  <NavLink
                    to="/bilebandi-juhliin"
                    aria-label="Bilebändi — häät, bileet, isot juhlat"
                    className="hover:text-electric"
                  >
                    🎤 Bilebändi
                    <span className="sr-only"> — häät, bileet, isot juhlat</span>
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/dj-juhliin"
                    aria-label="DJ — juhlat, klubit, yritystilaisuudet"
                    className="hover:text-electric"
                  >
                    🎧 DJ
                    <span className="sr-only"> — juhlat, klubit, yritystilaisuudet</span>
                  </NavLink>
                </li>

                <li>
                  <NavLink
                    to="/trubaduuri-juhliin"
                    aria-label="Trubaduuri — illanistujaiset, pienet juhlat"
                    className="hover:text-electric"
                  >
                    🎸 Trubaduuri
                    <span className="sr-only"> — illanistujaiset, pienet juhlat</span>
                  </NavLink>
                </li>
              </ul>
            </div>
            {/* Palvelu */}
            <div>
              <h4 className="font-customFont2 text-xl text-white uppercase mb-3">Palvelu</h4>
              <ul className="space-y-2 text-white/80 font-customFont1 text-xs md:text-lg">
                <li>
                  {/* SPA-navigaatio */}
                  <NavLink to="/useinkysyttya" className="hover:text-electric">UKK</NavLink>
                </li>
                <li>
                  {/* Sama osoite kuin päävalikossa */}
                  <NavLink to={BLOG_ROUTE} className="hover:text-electric">
                    Blogi
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/tietoameista" className="hover:text-electric">Tietoa meistä</NavLink>
                </li>

                  <li>
                  <NavLink to="/yhteistyo" className="hover:text-electric">
                    Yhteistyö & kumppanit
                  </NavLink>
                </li>
              </ul>
            </div>

            {/* Esiintyjille */}
            <div>
              <h4 className="font-customFont2 text-xl text-white uppercase mb-3">Esiintyjille</h4>
              <ul className="space-y-2 text-white/80 font-customFont1 text-xs md:text-lg">
                <li>
                  {/* Tämä oli aiemmin <a href> → johti WP:n valkoiseen sivuun */}
                  <NavLink to="/ukk-esiintyjille" className="hover:text-electric">
                    UKK-esiintyjille
                  </NavLink>
                </li>

                {/* Hallintapaneeli vain kirjautuneille, mutta ei onboardingissa */}
                {canShowDashboard && (
                  <li>
                    <NavLink
                      to={`/dashboard?${DASH_USER_QS}`} // band=user&tab=uprofile
                      className="hover:text-electric"
                      aria-label="Hallintapaneeli"
                      title="Hallintapaneeli"
                    >
                      Hallintapaneeli
                    </NavLink>
                  </li>
                )}

                {/* Kirjaudu/Rekisteröidy vain ei-kirjautuneille */}
                {!isAuthed && (
                  <>
                    <li>
                      <NavLink to="/kirjaudu" className="hover:text-electric">
                        Kirjaudu sisään
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/rekisteroidy" className="hover:text-electric">
                        Luo tili / Rekisteröidy
                      </NavLink>
                    </li>
                  </>
                )}
              </ul>
            </div>

            {/* Yhteystiedot */}
            <div>
              <h4 className="font-customFont2 text-xl text-white uppercase mb-3">Yhteystiedot</h4>
              <ul className="space-y-2 text-white/80 font-customFont1 text-xs md:text-lg">
                <li className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-electric" aria-hidden="true" />
                  <span>Kerava, Uusimaa</span>
                </li>
                <li className="flex items-start gap-2">
                  <Phone className="h-4 w-4 mt-0.5 text-electric" aria-hidden="true" />
                  {/* Ulkoiset linkit jäävät <a>-tageiksi */}
                  <a href="tel:+358443519265" className="hover:text-electric">+358 44 351 92 65</a>
                </li>
                <li className="flex items-start gap-2">
                  <Mail className="h-4 w-4 mt-0.5 text-electric" aria-hidden="true" />
                  <a href="mailto:info@buukkeri.fi" className="hover:text-electric">info@buukkeri.fi</a>
                </li>
              </ul>
            </div>

            {/* Seuraa */}
            <div>
              <h4 className="font-customFont2 text-xl text-white mb-3">Seuraa</h4>

              {/* ⬇️ Keskitetty SocialLinks-komponentti (sis. GA4-eventit & animaatiot) ⬇️ */}
              <SocialLinks variant="footer" />
              {/* ⬆️ /SocialLinks ⬆️ */}

              <p className="mt-3 text-white/70 font-customFont1 text-sm md:text-base flex items-center gap-2">
                <Music4 className="h-4 w-4 text-pink" aria-hidden="true" />
                Löydä esiintyjät tapahtumaasi — helposti.
              </p>
            </div>
          </div>

         {/* Alapalkki */}
          <div className="mt-10 flex flex-col md:flex-row items-center justify-evenly gap-3 border-t border-white/10 pt-6">
            <p className="text-white/60 text-sm md:text-base font-customFont1">
              © {new Date().getFullYear()} <span className="font-medium">Buukkeri</span>. Kaikki oikeudet pidätetään.
            </p>

            <div className="flex items-center gap-4 text-white/60 font-customFont1 text-sm md:text-base">
              <NavLink
                to="/tietosuoja"
                className="hover:text-electric"
                onClick={() => {
                  window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                }}
              >
                Tietosuoja
              </NavLink>

              <span className="opacity-50">•</span>

              <NavLink
                to="/kayttoehdot"
                className="hover:text-electric"
                onClick={() => {
                  window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                }}
              >
                Käyttöehdot
              </NavLink>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
